import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  RealEstateKey,
  PricingModel,
  CategoryMultipliers,
  VolumeDiscounts,
  VolumeDiscountTier,
  REAL_ESTATE_LABELS,
} from "../types";

interface RealEstatePricingTableProps {
  pricingModel: Record<RealEstateKey, PricingModel>;
  categoryMultipliers: CategoryMultipliers;
  volumeDiscounts: VolumeDiscounts;
  onModelChange: (model: Record<RealEstateKey, PricingModel>) => void;
  onMultipliersChange: (mult: CategoryMultipliers) => void;
  onVolumeDiscountsChange: (disc: VolumeDiscounts) => void;
}

export const RealEstatePricingTable = ({
  pricingModel,
  categoryMultipliers,
  volumeDiscounts,
  onModelChange,
  onMultipliersChange,
  onVolumeDiscountsChange,
}: RealEstatePricingTableProps) => {
  const [expandedKey, setExpandedKey] = useState<RealEstateKey | null>(null);

  const toggle = (key: RealEstateKey) => {
    onModelChange({
      ...pricingModel,
      [key]: pricingModel[key] === "m2" ? "unit" : "m2",
    });
  };

  const addDiscountTier = (key: RealEstateKey) => {
    const current = [...(volumeDiscounts[key] || [])];
    const lastFrom = current.length > 0 ? current[current.length - 1].from : 0;
    const isM2 = pricingModel[key] === "m2";
    current.push({ from: lastFrom + (isM2 ? 5000 : 100), discountPct: 10 });
    onVolumeDiscountsChange({ ...volumeDiscounts, [key]: current });
  };

  const updateDiscountTier = (key: RealEstateKey, idx: number, field: keyof VolumeDiscountTier, value: number) => {
    const current = [...(volumeDiscounts[key] || [])];
    current[idx] = { ...current[idx], [field]: value };
    onVolumeDiscountsChange({ ...volumeDiscounts, [key]: current });
  };

  const removeDiscountTier = (key: RealEstateKey, idx: number) => {
    const current = [...(volumeDiscounts[key] || [])];
    current.splice(idx, 1);
    onVolumeDiscountsChange({ ...volumeDiscounts, [key]: current });
  };

  const reKeys = Object.keys(REAL_ESTATE_LABELS) as RealEstateKey[];

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]"></TableHead>
            <TableHead>Type vastgoed</TableHead>
            <TableHead className="text-center w-[80px]">Model</TableHead>
            <TableHead className="text-right w-[120px]">Prijsfactor</TableHead>
            <TableHead className="text-right w-[280px]">Volumekorting</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reKeys.map((key) => {
            const info = REAL_ESTATE_LABELS[key];
            const isM2 = pricingModel[key] === "m2";
            const mult = categoryMultipliers[key] ?? 1.0;
            const pct = Math.round(mult * 100);
            const discounts = volumeDiscounts[key] || [];
            const isExpanded = expandedKey === key;
            const unitLabel = isM2 ? "m\u00B2" : "units";

            return (
              <TableRow key={key} className="align-top">
                <TableCell className="text-center text-lg pt-3">{info.icon}</TableCell>
                <TableCell className="font-medium text-sm pt-3">{info.label}</TableCell>
                <TableCell className="text-center pt-2.5">
                  <button
                    onClick={() => toggle(key)}
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                      isM2
                        ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    }`}
                  >
                    {isM2 ? "m\u00B2" : "unit"}
                  </button>
                </TableCell>
                <TableCell className="text-right pt-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <Input type="number" step="5" min="0" max="300" value={pct}
                      onChange={(e) => { const v = parseInt(e.target.value); onMultipliersChange({ ...categoryMultipliers, [key]: (isNaN(v) ? 100 : v) / 100 }); }}
                      className="w-20 h-8 text-sm text-right" />
                    <span className="text-xs text-neutral-secondary-mid">%</span>
                  </div>
                </TableCell>
                <TableCell className="text-right pt-2">
                  <button
                    onClick={() => setExpandedKey(isExpanded ? null : key)}
                    className="text-[10px] text-primary hover:underline"
                  >
                    {discounts.length > 0
                      ? `${discounts.length} staffel(s)`
                      : "Geen korting"}
                    {isExpanded ? " \u25B2" : " \u25BC"}
                  </button>
                  {isExpanded && (
                    <div className="mt-2 text-left space-y-1">
                      {discounts.map((d, idx) => {
                        const resultPct = Math.round(pct * (1 - d.discountPct / 100));
                        const prevFrom = idx > 0 ? discounts[idx - 1].from : 0;
                        const hasError = d.from <= 0 || (idx > 0 && d.from <= prevFrom);
                        const errorMsg = d.from <= 0
                          ? "Moet hoger dan 0 zijn"
                          : (idx > 0 && d.from <= prevFrom)
                            ? `Moet hoger zijn dan ${prevFrom}`
                            : null;
                        return (
                          <div key={idx}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-neutral-secondary-mid whitespace-nowrap">vanaf</span>
                              <Input type="number" min="1" value={d.from}
                                onChange={(e) => updateDiscountTier(key, idx, "from", parseInt(e.target.value) || 0)}
                                className={`w-24 h-8 text-sm text-right ${hasError ? "border-destructive" : ""}`} />
                              <span className="text-xs text-neutral-secondary-mid whitespace-nowrap">{unitLabel} &rarr;</span>
                              <Input type="number" min="0" max="100" value={d.discountPct}
                                onChange={(e) => updateDiscountTier(key, idx, "discountPct", parseInt(e.target.value) || 0)}
                                className="w-16 h-8 text-sm text-right" />
                              <span className="text-xs text-neutral-secondary-mid">%</span>
                              <span className="text-xs font-medium text-emerald-600 whitespace-nowrap">
                                = {resultPct}%
                              </span>
                              <Button variant="ghost" size="sm"
                                className="h-7 w-7 p-0 text-destructive/40 hover:text-destructive"
                                onClick={() => removeDiscountTier(key, idx)}>
                                <Trash size={14} />
                              </Button>
                            </div>
                            {errorMsg && (
                              <p className="text-[10px] text-destructive ml-10 mt-0.5">{errorMsg}</p>
                            )}
                          </div>
                        );
                      })}
                      <Button variant="ghost" size="sm" onClick={() => addDiscountTier(key)}
                        className="h-7 text-xs gap-1 text-primary">
                        <Plus size={12} /> Staffel toevoegen
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className="px-4 py-2 border-t text-[10px] text-neutral-secondary-mid">
        Prijsfactor: bv. magazijn 50% van basisprijs. Volumekorting: niet cumulatief, hoogste staffel geldt.
      </div>
    </div>
  );
};
